import { DateTime } from "luxon";
import { SharedApiV2 } from "@capstone/mock-api";
import { Site } from "src/app/core/sites";
import { DockOverview } from "src/app/partner/settings/docks/dock-overview.model";
import { DoorGroup } from "src/app/partner/settings/door-groups/door-group.model";
import { Door } from "src/app/partner/settings/doors/door.model";
import { Vendor } from "src/app/partner/vendors/vendor.model";
import { getEnumMember, isExistent, parseDateTime } from "src/utils";
import { AppointmentReservationSlot } from "./appointment-reservation-slot.model";
import { AppointmentSlotDock } from "./appointment-slot-dock.model";
import { AppointmentReservationSlotRequest } from "./appointment-slot-request.model";
import { AppointmentSlot } from "./appointment-slot.model";

export class AppointmentSlotInformation {
  private constructor(args: ClassProperties<AppointmentSlotInformation>) {
    this.cutoffMessages = args.cutoffMessages;
    this.date = args.date;
    this.docks = args.docks;
    this.exceededVendorLimits = args.exceededVendorLimits;
    this.groupedMessages = args.groupedMessages;
    this.hasNoDefaultDoorGroup = args.hasNoDefaultDoorGroup;
    this.isDateInSiteDeliveryWindow = args.isDateInSiteDeliveryWindow;
    this.slots = args.slots;
  }

  public readonly cutoffMessages: readonly string[] | null;
  public readonly date: DateTime;
  public readonly docks: readonly AppointmentSlotDock[];
  public readonly exceededVendorLimits: readonly VendorLimit[] | null;
  public readonly groupedMessages: SlotGroupedMessages;
  public readonly hasNoDefaultDoorGroup: boolean;
  public readonly isDateInSiteDeliveryWindow: boolean;
  public readonly slots: readonly AppointmentSlot[];

  public static deserialize(
    data: SharedApiV2.UnreservedSlotResultsDto,
    { docks, doorGroups, doors, site }: UnreservedSlotsDeserializeArguments,
  ): AppointmentSlotInformation {
    if (!data.date) {
      throw new Error("Missing date on unreserved slots response.");
    }

    const { groupedMessages, messages } =
      deserializeAppointmentSlotMessages(data);

    return new AppointmentSlotInformation({
      cutoffMessages:
        data.cutoffMessages.length > 0
          ? data.cutoffMessages.map(({ text }) => text)
          : null,
      date: parseDateTime(data.date),
      docks: AppointmentSlotDock.deserializeList(
        // Hourly-capacity sites report their dock capacities under
        // `hourlyCapacities` and leave `docks` empty.
        data.hourlyCapacities.length > 0 ? data.hourlyCapacities : data.docks,
        { docks, doorGroups },
      ),
      exceededVendorLimits:
        data.vendors.length > 0
          ? data.vendors.map((limits) => ({
              actualLoadCount: limits.loadCount,
              loadsOverLimit: limits.loadCount - limits.maxLoadCount,
              maximumLoadCount: limits.maxLoadCount,
              vendor: { id: limits.vendorId, name: limits.name },
            }))
          : null,
      groupedMessages,
      hasNoDefaultDoorGroup: messages.some(
        ({ code }) =>
          code === AppointmentSlotInformationMessageCode.NoDoorGroup,
      ),
      isDateInSiteDeliveryWindow: data.isDateInSiteDeliveryWindow,
      slots: AppointmentSlot.deserializeList(data.slots, {
        doors,
        site,
      }),
    });
  }

  public static deserializeList(
    values: SharedApiV2.UnreservedSlotResultsDto[],
    { docks, doorGroups, doors, site }: UnreservedSlotsDeserializeArguments,
  ): AppointmentSlotInformation[] {
    return values.map((x) =>
      AppointmentSlotInformation.deserialize(x, {
        docks,
        doorGroups,
        doors,
        site,
      }),
    );
  }

  /**
   * Whether slots exist for the given dock.
   * @param dock - The dock to check for slots.
   */
  public hasSlots(dock: DockOverview): boolean {
    return this.slots.some((slot) =>
      slot.doors.some((door) => door.dock.id === dock.id),
    );
  }
}

interface UnreservedSlotsDeserializeArguments {
  readonly docks: readonly DockOverview[];
  readonly doorGroups: readonly DoorGroup[];
  readonly doors: readonly Door[];
  readonly site: Site;
}

interface VendorLimit {
  readonly actualLoadCount: number;
  readonly loadsOverLimit: number;
  readonly maximumLoadCount: number;
  readonly vendor: Pick<Vendor, "id" | "name">;
}

export class AppointmentReservationSlotInformation {
  private constructor(
    args: ClassProperties<AppointmentReservationSlotInformation>,
  ) {
    this.conformantSlots = args.conformantSlots;
    this.date = args.date;
    this.docks = args.docks;
    this.slots = args.slots;
  }

  /** Slots that conform to the rules. */
  public readonly conformantSlots: readonly AppointmentReservationSlot[];
  public readonly date: DateTime;
  public readonly docks: readonly AppointmentReservationSlotDock[];
  /** All slots, even those that violate rules. */
  public readonly slots: readonly AppointmentReservationSlot[];

  public static deserialize(
    data: SharedApiV2.ReservedSlotResultsDto,
    { docks, request, doors, site }: ReservedSlotsDeserializeArguments,
  ): AppointmentReservationSlotInformation {
    if (!data.date) {
      throw new Error("Missing date on reserved slots response.");
    }

    const slots = AppointmentReservationSlot.deserializeList(data.slots, {
      request,
      site,
      doors,
    });
    return new AppointmentReservationSlotInformation({
      conformantSlots: slots.filter(
        (slot) =>
          !slot.ruleViolations?.ordersManagedType &&
          (request.isDropLoadAllowed ||
            !slot.ruleViolations?.dropLoadAvailability),
      ),
      date: parseDateTime(data.date),
      docks: data.docks.map((dockData) => {
        const dock = docks.find(({ id }) => id === dockData.dockId);
        if (!dock) {
          throw new Error(`Could not find dock with ID "${dockData.dockId}".`);
        }
        return { dock };
      }),
      slots,
    });
  }

  public static deserializeList(
    values: SharedApiV2.ReservedSlotResultsDto[],
    { docks, request, doors, site }: ReservedSlotsDeserializeArguments,
  ): AppointmentReservationSlotInformation[] {
    return values.map((x) =>
      AppointmentReservationSlotInformation.deserialize(x, {
        docks,
        request,
        doors,
        site,
      }),
    );
  }

  /**
   * Whether a given dock has reservations that apply to the specific slot
   * request that generated these slot results.
   *
   * @param dock - The dock to check for applicable reservations.
   */
  public hasApplicableReservations(dock: DockOverview): boolean {
    // Based on the API logic, if the slot dock exists in the response, then
    // there are reservations that apply for that dock.
    return this.docks.some((slotDock) => slotDock.dock.id === dock.id);
  }

  /**
   * Whether slots exist for the given dock.
   * @param dock - The dock to check for slots.
   */
  public hasSlots(dock: DockOverview): boolean {
    return this.slots.some((slot) =>
      slot.doors.some((door) => door.dock.id === dock.id),
    );
  }
}

type SlotGroupedMessages = {
  -readonly [key in keyof typeof AppointmentSlotInformationGroupedMessages]:
    string[] | null;
};

interface AppointmentReservationSlotDock {
  readonly dock: DockOverview;
}

interface ReservedSlotsDeserializeArguments {
  readonly docks: readonly DockOverview[];
  readonly request: AppointmentReservationSlotRequest;
  readonly doors: readonly Door[];
  readonly site: Site;
}

export enum AppointmentSlotInformationMessageCode {
  DockClosedAtSelectedTime = "Closures_Dock",
  DockDateThresholdExceeded = "DOCK_DATE_THRESHOLD",
  DoorClosedAtSelectedTime = "Closures_Door",
  DoorMaximumThresholdNotMet = "Door_maximum_Settings",
  DoorMinimumThresholdNotMet = "Door_Minimum_Settings",
  EquipmentNotAvailable = "Equipment_Not_Available",
  InvalidDoorGroup = "INVALID_DOORGROUP",
  MaxVendorCountExceeded = "Max_Vendor_Count_Exceeded",
  NoDockCapacity = "No_Capacity_For_Dock",
  NoDoorGroup = "NO_DDG",
  NoDoorsLinkedToDoorGroup = "No_Doors_For_Door_Group",
  // The message related to this code won't be displayed on the UI,
  // and it will be removed from API code eventually as discussed.
  // It's been added here to avoid console error on deserialization.
  OverlappingWithBookedReservation = "Reservation_Overlapping",
  PastDockCutOff = "DOCK_CUTOFF",
  PastDockCutOffV1 = "PastDockCutOff",
  SameDockRestriction = "SAME_DOCK_RESTRICTION",
  SiteClosedAtSelectedTime = "Closures_Site",
  SlotClosedForCarrier = "Slot_Closed_For_Carrier",
  SlotClosedForDeliveryCarrier = "Slot_Closed_For_Delivery_Carrier",
  SlotClosedForVendor = "Slot_Closed_For_Vendor",
  // Code for guided users "Vendor Same Day Appointment Not Allowed" error message
  // (it won't be displayed on the UI.)
  VendorSameDayAppointmentNotAllowed = "Vendor_Same_Day_Appointment",
}

export enum AppointmentSlotInformationGroupedMessages {
  carriersDeliveryWindowMessages = AppointmentSlotInformationMessageCode.SlotClosedForCarrier,
  deliveryCarrierDeliveryWindowMessages = AppointmentSlotInformationMessageCode.SlotClosedForDeliveryCarrier,
  dockClosedAtSelectedTimeMessages = AppointmentSlotInformationMessageCode.DockClosedAtSelectedTime,
  dockDateThresholdExceededMessages = AppointmentSlotInformationMessageCode.DockDateThresholdExceeded,
  doorClosedAtSelectedTimeMessages = AppointmentSlotInformationMessageCode.DoorClosedAtSelectedTime,
  doorMaximumThresholdNotMetMessages = AppointmentSlotInformationMessageCode.DoorMaximumThresholdNotMet,
  doorMinimumThresholdNotMetMessages = AppointmentSlotInformationMessageCode.DoorMinimumThresholdNotMet,
  equipmentNotAvailableMessages = AppointmentSlotInformationMessageCode.EquipmentNotAvailable,
  maxVendorCountExceededMessages = AppointmentSlotInformationMessageCode.MaxVendorCountExceeded,
  noDoorsLinkedToDoorGroupMessages = AppointmentSlotInformationMessageCode.NoDoorsLinkedToDoorGroup,
  noDockCapacityMessages = AppointmentSlotInformationMessageCode.NoDockCapacity,
  pastDockCutOffMessages = AppointmentSlotInformationMessageCode.PastDockCutOff,
  siteClosedAtSelectedTimeMessages = AppointmentSlotInformationMessageCode.SiteClosedAtSelectedTime,
  vendorsDeliveryWindowMessages = AppointmentSlotInformationMessageCode.SlotClosedForVendor,
}

export function deserializeAppointmentSlotMessages(data: SlotMessagesData): {
  messages: Array<{
    code: AppointmentSlotInformationMessageCode;
    text: string;
  }>;
  groupedMessages: SlotGroupedMessages;
} {
  const messages = data.messages
    .map((message) => ({
      ...message,
      code: normalizeSlotMessageCode(
        getEnumMember(
          AppointmentSlotInformationMessageCode,
          message.code,
          null,
        ),
      ),
    }))
    .filter(
      (
        message,
      ): message is {
        code: AppointmentSlotInformationMessageCode;
        text: string;
      } => isExistent(message.code),
    );

  const groupedMessages: SlotGroupedMessages = {
    carriersDeliveryWindowMessages: null,
    deliveryCarrierDeliveryWindowMessages: null,
    dockClosedAtSelectedTimeMessages: null,
    dockDateThresholdExceededMessages: null,
    doorClosedAtSelectedTimeMessages: null,
    doorMaximumThresholdNotMetMessages: null,
    doorMinimumThresholdNotMetMessages: null,
    equipmentNotAvailableMessages: null,
    maxVendorCountExceededMessages: null,
    noDoorsLinkedToDoorGroupMessages: null,
    noDockCapacityMessages: null,
    pastDockCutOffMessages: null,
    siteClosedAtSelectedTimeMessages: null,
    vendorsDeliveryWindowMessages: null,
  };

  Object.entries(AppointmentSlotInformationGroupedMessages).forEach(
    ([key, value]) => {
      groupedMessages[key] = getTextsFromMessages(messages, value.toString());
    },
  );

  return {
    messages,
    groupedMessages,
  };
}

interface SlotMessagesData {
  messages: ReadonlyArray<{ code: string; text: string }>;
}

function normalizeSlotMessageCode(
  code: AppointmentSlotInformationMessageCode | null,
): AppointmentSlotInformationMessageCode | null {
  return code === AppointmentSlotInformationMessageCode.PastDockCutOffV1
    ? AppointmentSlotInformationMessageCode.PastDockCutOff
    : code;
}

function getTextsFromMessages(
  messages: Array<{
    code: AppointmentSlotInformationMessageCode;
    text: string;
  }>,
  messageCode: string,
): string[] | null {
  const texts = messages
    .filter(({ code }) => code.toString() === messageCode)
    .map(({ text }) => text);
  return texts.length ? texts : null;
}
